<template>
  <div class="py-6 px-8">
    <h-header>
      <template v-slot:actions>
        <v-menu bottom rounded offset-y>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" elevation="0" v-on="on">
              <v-icon left>mdi-plus</v-icon>
              <span class="d-none d-md-flex body-2">
                {{ $t('appointment.adminActions.options') }}
              </span>
            </v-btn>
          </template>
          <v-card>
            <v-list dense>
              <span v-for="(option) in options">
                <v-list-item @click="action(option.modal)" v-if="option.show" link>
                  <v-icon class="mr-3 grey--text text--lighten-1" style="transform: scale(0.85); opacity: 0.75">
                    {{ option.icon }}
                  </v-icon>
                  <span class="body-2">
                    {{ option.title }}
                  </span>
                </v-list-item>
              </span>
            </v-list>
          </v-card>
        </v-menu>
      </template>
    </h-header>

    <AppointmentReschedule isAdmin />
    <AdminAppointmentChangeHost />
    <AdminAppointmentChangeStatus />
    <AdminAppointmentChangeType :key="appointment.type" />
    <AdminAppointmentChangeKeyLocation />
    <ModalAppointmentVisitorEdit isAdmin />
    <AdminAppointmentQuoteReopen />
    <AdminAppointmentAddHost />
    <AdminAppointmentChangeDate />
    <AppointmentUpdateObservations />
    <AppointmentUpdateDisplacement />
    <ModalBulkSendConfirmationEmail :selectedAppointments="[appointment]" :mapAction="false" />
    <ModalBulkSendRejectedEmail :selectedAppointments="[appointment]" :mapAction="false" />
    <ModalUpdateAdditionalInfo v-if="appointment" @refreshMap="refreshAppointment" :additional-data="additionalData" />

    <v-tabs v-model="currentTab" color="realEstate">
      <v-tab>{{ $t('appointment.details.tabs.details') }}</v-tab>
      <v-tab>{{ $t('appointment.details.tabs.history') }}</v-tab>
      <v-tab>{{ $t('appointment.details.tabs.questionary') }}</v-tab>
      <v-tab v-if="showFinancialTab">{{ $t('appointment.details.tabs.financial') }}</v-tab>
      <v-tab v-if="serviceReport">{{ $t('appointment.details.tabs.reportAndQuotes') }}</v-tab>
      <v-tab v-if="showDispute">{{ $t('board.actions.dispute') }}</v-tab>
    </v-tabs>

    <v-card elevation="0" outlined>
      <h-loading v-if="loading" />
      <v-tabs-items v-else v-model="currentTab">
        <v-tab-item>
          <AdminAppointmentServiceDetails />
        </v-tab-item>
        <v-tab-item>
          <AppointmentHistory />
        </v-tab-item>
        <v-tab-item>
          <AppointmentQuestionnaire isAdmin />
        </v-tab-item>
        <v-tab-item v-if="showFinancialTab">
          <AdminAppointmentFinancialDetails :appointment-id="id" />
        </v-tab-item>
        <v-tab-item v-if="serviceReport">
          <AdminAppointmentReportAndQuotes />
        </v-tab-item>
        <v-tab-item v-if="showDispute">
          <AppointmentDispute />
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <AppointmentCancel isAdmin />
  </div>
</template>

<script>
import AppointmentReschedule from '@/components/appointment/AppointmentReschedule'
import AdminAppointmentServiceDetails from '@/components/appointment/AdminAppointmentServiceDetails'
import AdminAppointmentChangeHost from '@/components/appointment/AdminAppointmentChangeHost'
import AdminAppointmentAddHost from '@/components/appointment/AdminAppointmentAddHost'
import AdminAppointmentChangeStatus from '@/components/appointment/AdminAppointmentChangeStatus'
import AdminAppointmentChangeType from '@/components/appointment/AdminAppointmentChangeType'
import AdminAppointmentChangeKeyLocation from '@/components/appointment/AdminAppointmentChangeKeyLocation'
import AdminAppointmentFinancialDetails from '@/components/appointment/AdminAppointmentFinancialDetails'
import AppointmentHistory from '@/components/appointment/AppointmentHistory'
import AppointmentQuestionnaire from '@/components/appointment/AppointmentQuestionnaire'
import AdminAppointmentReportAndQuotes from '@/components/appointment/AdminAppointmentReportAndQuotes'
import ModalAppointmentVisitorEdit from '@/components/appointment/modals/ModalAppointmentVisitorEdit'
import AdminAppointmentQuoteReopen from '@/components/appointment/AdminAppointmentQuoteReopen'
import AppointmentCancel from '@/components/appointment/AppointmentCancel'
import AdminAppointmentChangeDate from '@/components/appointment/AdminAppointmentChangeDate'
import AppointmentUpdateObservations from '../components/appointment/AppointmentUpdateObservations'
import AppointmentUpdateDisplacement from '../components/appointment/AppointmentUpdateDisplacement'
import ModalUpdateAdditionalInfo from '@/components/appointment/modals/ModalUpdateAdditionalInfo'

import { mapGetters, mapActions, mapMutations } from 'vuex'
import { visitTypeServices, rentOutTypes } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'
import ModalBulkSendConfirmationEmail from '../components/appointment/modals/ModalBulkSendConfirmationEmail.vue'
import ModalBulkSendRejectedEmail from '../components/appointment/modals/ModalBulkSendRejectedEmail.vue'

import AppointmentDispute from '../components/appointment/AppointmentDispute.vue'

export default {
  name: 'AdminAppointmentDetails',

  components: {
    AppointmentReschedule,
    AppointmentHistory,
    AdminAppointmentServiceDetails,
    AppointmentQuestionnaire,
    AdminAppointmentFinancialDetails,
    AdminAppointmentChangeHost,
    AdminAppointmentChangeStatus,
    AdminAppointmentReportAndQuotes,
    AdminAppointmentChangeType,
    AdminAppointmentChangeKeyLocation,
    ModalAppointmentVisitorEdit,
    AdminAppointmentQuoteReopen,
    AdminAppointmentAddHost,
    AppointmentCancel,
    AdminAppointmentChangeDate,
    AppointmentUpdateObservations,
    AppointmentUpdateDisplacement,
    ModalBulkSendConfirmationEmail,
    ModalBulkSendRejectedEmail,
    ModalUpdateAdditionalInfo,
    AppointmentDispute,
  },

  data() {
    return {
      currentTab: 0,
      returnRoute: '../admin-appointments',
      id: this.$router.currentRoute.params.id,

      loading: false,
    }
  },

  computed: {
    ...mapGetters(['appointment', 'serviceReport']),

    additionalData() {
      return this.appointment?.additional_data ?? []
    },

    isVisitType() {
      return visitTypeServices.includes(this.appointment?.type)
    },

    isRentOut() {
      return rentOutTypes.includes(this.appointment?.type)
    },

    hasHost() {
      return this.appointment?.host !== null
    },

    /** A reopened job back in the pool has no host, but may already carry a paid trip reimbursement. */
    showFinancialTab() {
      return this.hasHost || this.appointment?.payments?.length > 0
    },

    hasVisitorOrTenant() {
      return this.appointment?.visitor_name !== null
    },

    isNotCanceled() {
      return this.appointment.status !== 'CANCELED'
    },

    isRejected() {
      return this.appointment.status == 'REJECTED'
    },

    isNotDone() {
      return this.appointment.status !== 'DONE'
    },

    canChangeAppointmentType() {
      return this.appointment.status == 'OPEN' && !this.appointment.host
    },

    canChangeAppointmentStatus() {
      return !this.appointment.group_code
    },

    canChangeAppointmentKeyLocation() {
      return ['OPEN', 'SCHEDULED'].includes(this.appointment.status) && !this.appointment.started
    },

    canReopenQuote() {
      return this.isRentOut && ['SCHEDULED', 'DONE'].includes(this.appointment.status)
        && !this.isQuoteAlreadyPaid && this.isServiceReportFinished && !this.isHostAlreadyPaid
    },

    isHostAlreadyPaid() {
      // a trip reimbursement pays the host who travelled, not the job's host
      return this.appointment?.payments
        ?.filter(payment => payment.type !== 'DETACHED' && payment.status).length > 0
    },

    isQuoteAlreadyPaid() {
      return this.serviceReport?.quotes
        ? ['RECEIVED', 'CONFIRMED'].includes(this.serviceReport?.quotes[0]?.charge?.installments[0]?.status)
        : false
    },

    isServiceReportFinished() {
      return this.serviceReport?.finish_status && this.serviceReport?.finished_at
    },

    canAddHost() {
      return ['OPEN', 'DRAFT', 'REJECTED'].includes(this.appointment.status) && !this.appointment.host && !this.appointment.group_code
    },

    canChangeHost() {
      return ['SCHEDULED', 'REJECTED'].includes(this.appointment.status) && !this.appointment.group_code
    },

    canChangeDate() {
      return ['SCHEDULED', 'OPEN'].includes(this.appointment.status);
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },

    // Deliberately admin-only (unlike isAdminOrPartner above) — partners should not be able to
    // open the S3 console link, at least for now.
    isAdmin() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN')
    },

    options() {
      return [
        {
          show: true,
          modal: 'RESCHEDULE_APPOINTMENT_MODAL',
          icon: 'mdi-calendar-refresh-outline',
          title: this.$t('appointment.adminActions.reschedule'),
        },
        {
          show: this.canChangeHost,
          modal: 'CHANGE_APPOINTMENT_HOST_MODAL',
          icon: 'mdi-account-sync-outline',
          title: this.$t('appointment.adminActions.changeHost.action'),
        },
        {
          show: this.canChangeAppointmentStatus,
          modal: 'CHANGE_APPOINTMENT_STATUS_MODAL',
          icon: 'mdi-flag-outline',
          title: this.$t('appointment.adminActions.changeStatus.action'),
        },
        {
          show: this.canChangeAppointmentType && this.isNotCanceled,
          modal: 'CHANGE_APPOINTMENT_TYPE_MODAL',
          icon: 'mdi-briefcase-arrow-left-right-outline',
          title: this.$t('appointment.adminActions.changeType.action'),
        },
        {
          show: this.canChangeAppointmentKeyLocation && this.isNotCanceled,
          modal: 'CHANGE_APPOINTMENT_KEY_LOCATION_MODAL',
          icon: 'mdi-key-outline',
          title: this.$t('appointment.adminActions.changeKeyLocation.action'),
        },
        {
          show: true,
          fetchAppointment: true,
          modal: 'ADDITIONAL_INFO_APPOINTMENT_MODAL',
          icon: 'mdi-information-box-outline',
          title: this.$t('appointment.adminActions.additionalInfo'),
        },
        {
          show: this.hasVisitorOrTenant && this.isNotCanceled,
          modal: 'EDIT_VISITOR_MODAL',
          icon: 'mdi-account-details-outline',
          title: this.isVisitType ? this.$t('board.actions.editVisitor') : this.$t('board.actions.editTenant'),
        },
        {
          show: this.canReopenQuote,
          modal: 'REOPEN_QUOTE_MODAL',
          icon: 'mdi-file-undo-outline',
          title: this.$t('appointment.adminActions.quoteReopen.action'),
        },
        {
          show: this.canAddHost,
          modal: 'ADD_HOST_MODAL',
          icon: 'mdi-account-plus',
          title: this.$t('appointment.adminActions.addHost.action'),
        },
        {
          show: this.canChangeDate,
          modal: 'CHANGE_DATE_MODAL',
          icon: 'mdi-update',
          title: this.$t('appointment.adminActions.changeDate.action'),
        },
        {
          show: this.isNotCanceled && this.isNotDone,
          modal: 'CANCEL_APPOINTMENT_MODAL',
          icon: 'mdi-delete-outline',
          title: this.$t('board.actions.cancel'),
        },
        {
          show: this.isNotCanceled && this.isNotDone,
          modal: 'UPDATE_OBSERVATIONS_APPOINTMENT_MODAL',
          icon: 'mdi-text-box-edit-outline',
          title: this.$t('appointment.adminActions.updateObservations.action'),
        },
        {
          show: this.isAdminOrPartner,
          modal: () => window.open('../confirm-appointment/' + this.id + '?isChangeDateAllowed=true', '_blank'),
          icon: 'mdi-file-document-check-outline',
          title: this.$t('appointment.adminActions.showConfirmationPage'),
        },
        {
          show: this.isAdminOrPartner && this.isNotDone && this.isNotCanceled,
          modal: 'BULK_SEND_CONFIRMATION_EMAIL_MODAL',
          icon: 'mdi-email-check-outline',
          title: this.$t('appointment.adminActions.sendConfirmationEmail'),
        },
        {
          show: this.isAdminOrPartner && this.isRejected,
          modal: 'BULK_SEND_REJECTED_EMAIL_MODAL',
          icon: 'mdi-email-remove-outline',
          title: this.$t('mapDrawing.sendRejectedEmail'),
        },
        {
          show: this.showDispute,
          fetchAppointment: true,
          modal: null,
          icon: 'mdi-file-search-outline',
          title: this.$t('board.actions.dispute'),
          type: 'dispute',
        },
        {
          // company_id, not real_estate.id: the bucket is hcheck's, and it files everything under
          // hcheck's own Company id. Our real estate id is a different value entirely, so the old
          // link pointed at a prefix that simply doesn't exist. It's null for real estates that
          // were never synced to hcheck — which are also the ones with no report files at all.
          show: this.isAdmin && !!this.appointment.job_id && !!this.appointment?.real_estate?.company_id,
          // Links at the job level, not the exact asset-report subfolder —
          // that id isn't surfaced to this frontend today, and a job only
          // ever has a handful of report folders under it to click into.
          modal: () => window.open(
            `https://s3.console.aws.amazon.com/s3/buckets/${process.env.VUE_APP_S3_BUCKET}` +
            `?region=sa-east-1&prefix=companies/${this.appointment.real_estate.company_id}/jobs/${this.appointment.job_id}/`,
            '_blank',
          ),
          icon: 'mdi-folder-open-outline',
          title: this.$t('appointment.adminActions.viewReportFiles'),
        },
      ]
    },

    showDispute() {
      return this.isAdminOrPartner && this.appointment.job_id && this.appointment?.status === 'DONE'
    },
  },

  methods: {
    ...mapActions([
      'openModal',
      'fetchAppointmentAsAdmin',
      'fetchAppointmentQuestionnairesAsAdmin',
      'fetchServiceReportAsAdmin',
      'fetchAppointmentFilesAsAdmin'
    ]),

    ...mapMutations(['setAppointmentId']),

    action(action) {
      if (typeof action === 'function') {
        action()
        return
      }

      this.openModal(action)
    },

    refreshAppointment() {
      this.fetchAppointmentAsAdmin(this.id)
    },
  },

  async mounted() {
    this.loading = true
    this.fetchAppointmentAsAdmin(this.id).finally(() => this.loading = false)

    this.$nextTick(() => {
      this.setAppointmentId(this.id)
      this.fetchServiceReportAsAdmin(this.id)
      this.fetchAppointmentFilesAsAdmin(this.id)
      this.fetchAppointmentQuestionnairesAsAdmin(this.id)
    })
  },
}
</script>
